import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { SITE_INFO } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6 },
};

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      toast.success(res.data.message);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Us | Texoma Resorts Near Lake Texoma, Texas</title>
        <meta name="description" content="Contact Texoma Resorts for booking inquiries, group events, and questions. Located at 1642 FM1310, Denison, TX near Lake Texoma." />
      </Helmet>

      <section className="pt-28 pb-12 md:pt-32 md:pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="caption-text text-accent mb-3">Reach Out</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-light text-foreground tracking-tight">
              Contact Us
            </h1>
          </motion.div>
        </div>
      </section>

      <section data-testid="contact-section" className="pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            <div className="lg:col-span-3">
              <motion.div {...fadeUp}>
                <h2 className="font-heading text-2xl font-normal text-foreground mb-6">Send Us a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="font-body text-sm">Name *</Label>
                      <Input
                        data-testid="contact-name"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="font-body mt-1"
                      />
                    </div>
                    <div>
                      <Label className="font-body text-sm">Email *</Label>
                      <Input
                        data-testid="contact-email"
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="font-body mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="font-body text-sm">Phone</Label>
                    <Input
                      data-testid="contact-phone"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="font-body mt-1"
                    />
                  </div>
                  <div>
                    <Label className="font-body text-sm">Message *</Label>
                    <Textarea
                      data-testid="contact-message"
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className="font-body mt-1"
                      rows={5}
                    />
                  </div>
                  <Button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={loading}
                    className="bg-primary hover:bg-primary/90 text-white font-body rounded-full px-8"
                  >
                    {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                    Send Message
                  </Button>
                </form>
              </motion.div>
            </div>

            <div className="lg:col-span-2">
              <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.2 }}>
                <h2 className="font-heading text-2xl font-normal text-foreground mb-6">Get in Touch</h2>
                <div className="space-y-6">
                  <Card className="border border-border/50">
                    <CardContent className="p-5 flex items-start gap-4">
                      <Phone size={20} className="text-primary mt-1 shrink-0" />
                      <div>
                        <h3 className="font-heading text-base font-semibold text-foreground">Phone</h3>
                        <a href={`tel:${SITE_INFO.phoneRaw}`} className="font-body text-sm text-muted-foreground hover:text-primary">
                          {SITE_INFO.phone}
                        </a>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border border-border/50">
                    <CardContent className="p-5 flex items-start gap-4">
                      <Mail size={20} className="text-primary mt-1 shrink-0" />
                      <div>
                        <h3 className="font-heading text-base font-semibold text-foreground">Email</h3>
                        <a href={`mailto:${SITE_INFO.email}`} className="font-body text-sm text-muted-foreground hover:text-primary">
                          {SITE_INFO.email}
                        </a>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border border-border/50">
                    <CardContent className="p-5 flex items-start gap-4">
                      <MapPin size={20} className="text-primary mt-1 shrink-0" />
                      <div>
                        <h3 className="font-heading text-base font-semibold text-foreground">Address</h3>
                        <p className="font-body text-sm text-muted-foreground">{SITE_INFO.address}</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border border-border/50">
                    <CardContent className="p-5 flex items-start gap-4">
                      <Clock size={20} className="text-primary mt-1 shrink-0" />
                      <div>
                        <h3 className="font-heading text-base font-semibold text-foreground">Business Hours</h3>
                        <p className="font-body text-sm text-muted-foreground">Mon - Fri: 9:00 AM - 6:00 PM</p>
                        <p className="font-body text-sm text-muted-foreground">Sat - Sun: By Appointment</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
